/*
 * StammKundenAppQueries.ts
 *
 * Created by Dr. Maximillian Dornseif 2023-10-12 in huWaWi3 33.1.0
 * Copyright (c) 2020-2023 HUDORA GmbH
 */

import gql from 'graphql-tag'

import {
  CRM_FRAGMENT,
  EDI_FRAGMENT,
  KUNDEN_ARTNR_FRAGMENT,
  KUNDEN_PREIS_FRAGMENT,
  KUNDE_FRAGMENT,
  LIEFERADRESSE_FRAGMENT,
  VERSANDANWEISUNG_FRAGMENT,
} from '../fragments'

// Zur Kundenauswahl

export const SUGGESTIONS_QUERY = gql`
  query FulltextKundenSuche($term: String!) {
    fulltextSearch: hwKundeSearch(term: $term) {
      id
      designator
      title
      score
    }
  }
`
// Liste aller aktiven Kunden

export const KUNDEN_LIST_QUERY = gql`
  query AlleKunden($cursor: String) {
    nodes: hwKundePages(
      after: $cursor
      sort: { order: DESC, fields: updated_at }
      filter: { ist_aktiv: { eq: true } }
    ) {
      # TODO: neue Query
      edges {
        # cursor
        node {
          ...kundeFragment
          gruppe {
            name
            vertreter
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${KUNDE_FRAGMENT}
`
export const KUNDEN_LIST_QUERY_WITH_CRM = gql`
  query AlleKundenMitCrm($cursor: String) {
    nodes: hwKundePages(
      after: $cursor
      sort: { order: DESC, fields: updated_at }
      filter: { ist_aktiv: { eq: true } }
    ) {
      # TODO: neue Query
      edges {
        # cursor
        node {
          ...kundeFragment
          gruppe {
            name
            vertreter
          }
          crm {
            ...crmFragment
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${KUNDE_FRAGMENT}
  ${CRM_FRAGMENT}
`
// Einen Kunden anhand der Kundennummer (Designator) abrufen.

export const KUNDE_ENTITY_QUERY = gql`
  query EinKunde($nr: ID!) {
    entity: hwKunde(designator: $nr) {
      ...kundeFragment
    }
  }
  ${KUNDE_FRAGMENT}
`
// Kunden anlegen

// export const KUNDE_CREATE_TEMPLATE = { designator: 'SC10000' }
export const KUNDE_CREATE_MUTATION = gql`
  mutation KundeAnlegen($input: HwKundeInput!) {
    entity: hwKundeCreate(input: $input) {
      ...kundeFragment
    }
  }
  ${KUNDE_FRAGMENT}
`

export const KUNDE_EDIT_MUTATION = gql`
  mutation KundenAendern($designator: ID!, $input: HwKundePatchInput!, $oldRev: ID!) {
    patchInfo: hwKundePatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...kundeFragment
      }
      new {
        ...kundeFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${KUNDE_FRAGMENT}
`

export const CRM_QUERY = gql`
  query KundeInfo($nr: ID!) {
    hwKunde(designator: $nr) {
      entity: crm {
        ...crmFragment
      }
    }
  }
  ${CRM_FRAGMENT}
`

export const CRM_EDIT_MUTATION = gql`
  mutation KundenCrmAendern($designator: ID!, $input: H3KundeInfoPatchInput!, $oldRev: ID!) {
    patchInfo: h3KundeInfoPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...crmFragment
      }
      new {
        ...crmFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${CRM_FRAGMENT}
`

export const EDI_QUERY = gql`
  query EdiConfigKunde($nr: ID!) {
    hwKunde(designator: $nr) {
      ...kundeFragment
      entity: ediConfig {
        ...ediFragment
      }
    }
  }
  ${KUNDE_FRAGMENT}
  ${EDI_FRAGMENT}
`

export const EDI_EDIT_MUTATION = gql`
  mutation KundenEdiAendern($designator: ID!, $input: HwKundeEdiConfigPatchInput!, $oldRev: ID!) {
    patchInfo: hwKundeEdiConfigPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...ediFragment
      }
      new {
        ...ediFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${EDI_FRAGMENT}
`

export const ARTNR_LIST_QUERY = gql`
  query KundeArtnrQuery($nr: ID!) {
    entity: hwKunde(designator: $nr) {
      ...kundeFragment
      nodes: kundenartikelnummern(filter: { ist_aktiv: { eq: true } }) {
        ...artnrFragment
      }
    }
  }
  ${KUNDE_FRAGMENT}
  ${KUNDEN_ARTNR_FRAGMENT}
`

export const ARTNR_CREATE_MUTATION = gql`
  mutation KundenartikelnummerAnlegen($input: HwKundenArtNrInput!) {
    entity: hwKundenArtNrCreate(input: $input) {
      ...artnrFragment
    }
  }
  ${KUNDEN_ARTNR_FRAGMENT}
`
export const ARTNR_EDIT_MUTATION = gql`
  mutation HwKundenArtNrAendern($designator: ID!, $input: HwKundenArtNrPatchInput!, $oldRev: ID!) {
    patchInfo: hwKundenArtNrPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...artnrFragment
      }
      new {
        ...artnrFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${KUNDEN_ARTNR_FRAGMENT}
`

export const KUNDEN_PREIS_QUERY = gql`
  query KundenpreiseKundeQuery($nr: ID!) {
    entity: hwKunde(designator: $nr) {
      ...kundeFragment
      nodes: kundenpreise(filter: { ist_aktiv: { eq: true } }) {
        ...preisFragment
      }
    }
  }
  ${KUNDE_FRAGMENT}
  ${KUNDEN_PREIS_FRAGMENT}
`

export const KUNDEN_UND_GRUPPE_PREIS_QUERY = gql`
  query KundenUndGruppenpreiseKundeQuery($nr: ID!) {
    entity: hwKunde(designator: $nr) {
      ...kundeFragment
      nodes: kundenpreise(filter: { ist_aktiv: { eq: true } }) {
        ...preisFragment
      }
      gruppe {
        id
        nodes: kundenpreise(filter: { ist_aktiv: { eq: true } }) {
          ...preisFragment
        }
      }
    }
  }
  ${KUNDE_FRAGMENT}
  ${KUNDEN_PREIS_FRAGMENT}
`

export const PREIS_CREATE_MUTATION = gql`
  mutation KundenPreisAnlegen($input: HwKundenPreisInput!) {
    entity: hwKundenPreisCreate(input: $input) {
      ...preisFragment
    }
  }
  ${KUNDEN_PREIS_FRAGMENT}
`
export const PREIS_EDIT_MUTATION = gql`
  mutation KundenpreisAendern($designator: ID!, $input: HwKundenPreisPatchInput!, $oldRev: ID!) {
    patchInfo: hwKundenPreisPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...preisFragment
      }
      new {
        ...preisFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${KUNDEN_PREIS_FRAGMENT}
`
export const LA_LIST_QUERY_MIT_KUNDE = gql`
  query KundeLieferadressenMitKunde($nr: ID!) {
    entity: hwKunde(designator: $nr) {
      ...kundeFragment
      nodes: lieferadressen {
        ...laFragment
      }
    }
  }
  ${KUNDE_FRAGMENT}
  ${LIEFERADRESSE_FRAGMENT}
`

export const LA_CREATE_MUTATION = gql`
  mutation LieferadresseAnlegen($input: HwLieferadresseInput!) {
    entity: hwLieferadresseCreate(input: $input) {
      ...laFragment
    }
  }
  ${LIEFERADRESSE_FRAGMENT}
`

export const LA_EDIT_MUTATION = gql`
  mutation updateHwKundeLieferadresse($designator: ID!, $input: HwLieferadressePatchInput!, $oldRev: ID!) {
    patchInfo: hwLieferadressePatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...laFragment
      }
      new {
        ...laFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${LIEFERADRESSE_FRAGMENT}
`

export const VA_ENTITY_QUERY = gql`
  query VersandanweisungKunde($nr: ID!) {
    hwKunde(designator: $nr) {
      ...kundeFragment
      entity: versandanweisung {
        ...versandanweisungFragment
      }
    }
  }
  ${KUNDE_FRAGMENT}
  ${VERSANDANWEISUNG_FRAGMENT}
`

export const VA_EDIT_MUTATION = gql`
  mutation updateVersandanweisung($designator: ID!, $input: HwVersandanweisungPatchInput!, $oldRev: ID!) {
    patchInfo: hwVersandanweisungPatch(designator: $designator, input: $input, oldRev: $oldRev) {
      old {
        ...versandanweisungFragment
      }
      new {
        ...versandanweisungFragment
      }
      oldRev
      newRev
      duration
      success
      errors
      audit {
        created_at
        typ
        uid
        user
        text
        new
        old
      }
    }
  }
  ${VERSANDANWEISUNG_FRAGMENT}
`
